// CRB3 (docs/build-briefs/CRB3-custom-report-sales-datasets-charts.md; design section 3; design/mockups/report-builder/03a, 02b and
// 04a): draws a custom report's chart as plain SVG from the pure model (reportChartModel.ts). No chart library. Every color is a
// class that reads a theme token (styles.css, `.cr-chart`), never a literal color; text is set with textContent only. The chart
// is a picture of the table under it: the svg carries a plain-words description for a screen reader, and a chart that cannot be
// drawn faithfully is N/A with the table still there.

import { CHART_LIMITS, chartDescription, compactValue, exactValue, type ChartModel, type ChartResult } from "./reportChartModel.ts";

const NS = "http://www.w3.org/2000/svg";

function mk(tag: string, attrs: Record<string, string | number>, parent?: Element): SVGElement {
  const node = document.createElementNS(NS, tag);
  for (const [key, value] of Object.entries(attrs)) node.setAttribute(key, String(value));
  parent?.append(node);
  return node as SVGElement;
}

function text(parent: Element, x: number, y: number, value: string, anchor: "start" | "middle" | "end", cls = ""): SVGElement {
  const node = mk("text", { x, y, "text-anchor": anchor, ...(cls === "" ? {} : { class: cls }) }, parent);
  node.textContent = value;
  return node;
}

/** The drawing is 1:1 with the card it sits in at the admin desktop size (DEC-424): 760 wide in the builder's result column,
 * 1340 wide on a saved report, which has the whole page. */
export const CHART_WIDTH_BUILDER = 760;
export const CHART_WIDTH_SAVED = 1340;
const H = 250;
const PAD = { left: 52, right: 12, top: 10, bottom: 26 };

/** The series class for the colors: the palette has six steps and a chart never has more series than that. */
const sc = (index: number): number => index % CHART_LIMITS.series;

function h(tag: string, cls: string, children: Array<Node | string> = [], testid?: string): HTMLElement {
  const node = document.createElement(tag);
  node.className = cls;
  if (testid !== undefined) node.dataset["testid"] = testid;
  for (const child of children) node.append(child);
  return node;
}

/** A label cut to fit `room` characters, with an ellipsis; the whole name stays in the mark's title. */
function fit(label: string, room: number): string {
  return label.length <= room ? label : `${label.slice(0, Math.max(room - 1, 1))}\u2026`;
}

function legend(model: ChartModel): HTMLElement | undefined {
  // A pie's key sits beside the pie, with each slice's figure and share, so it has no second key above.
  const entries = model.type !== "pie" && model.series.length > 1 ? model.series.map((s, index) => ({ label: s.label, index })) : [];
  if (entries.length === 0) return undefined;
  return h(
    "div",
    "cr-legend",
    entries.map((entry) => {
      const swatch = h("i", `cr-sw cr-sw-${sc(entry.index)}`);
      swatch.setAttribute("aria-hidden", "true");
      return h("span", "cr-legend-item", [swatch, entry.label]);
    }),
    "cr-chart-legend",
  );
}

function bars(svg: SVGElement, model: ChartModel, plot: { x: number; y: number; w: number; h: number }, yOf: (v: number) => number): void {
  const n = model.categories.length;
  const step = plot.w / n;
  const group = Math.min(step * 0.7, 56 * model.series.length);
  const stacked = model.type === "stacked_bar";
  const zero = yOf(0);
  for (let c = 0; c < n; c += 1) {
    const left = plot.x + step * c + (step - group) / 2;
    let base = zero;
    model.series.forEach((s, j) => {
      const value = s.values[c];
      if (value === null || value === undefined) return;
      const top = stacked ? base - (zero - yOf(value)) : yOf(value);
      const y = Math.min(top, stacked ? base : zero);
      const height = Math.abs((stacked ? base : zero) - top);
      const width = stacked ? group : group / model.series.length;
      const x = stacked ? left : left + j * width;
      const rect = mk(
        "rect",
        {
          x,
          y,
          width: Math.max(width - (model.series.length > 1 && !stacked ? 2 : 0), 1),
          height: Math.max(height, value === 0 ? 0 : 1),
          rx: 2,
          class: `cr-mark cr-fill-${sc(j)}`,
          "data-testid": "cr-chart-mark",
          "data-value": String(value),
          "data-category": model.categories[c] ?? "",
          "data-series": s.label,
        },
        svg,
      );
      const title = mk("title", {}, rect);
      title.textContent = `${model.categories[c] ?? ""}${model.series.length > 1 ? `, ${s.label}` : ""}: ${exactValue(value, model.unit)}`;
      if (stacked) base = top;
    });
  }
}

function lines(svg: SVGElement, model: ChartModel, plot: { x: number; y: number; w: number; h: number }, yOf: (v: number) => number): void {
  const n = model.categories.length;
  const step = plot.w / n;
  const xOf = (c: number): number => plot.x + step * c + step / 2;
  model.series.forEach((s, j) => {
    // A missing value breaks the line: it is never drawn as zero.
    let segment: string[] = [];
    const flush = (): void => {
      if (segment.length > 1) {
        mk("polyline", { points: segment.join(" "), fill: "none", "stroke-width": j === 0 ? 2.25 : 1.75, "stroke-linejoin": "round", "stroke-linecap": "round", class: `cr-line cr-stroke-${sc(j)}`, "data-testid": "cr-chart-line", "data-series": s.label }, svg);
      }
      segment = [];
    };
    s.values.forEach((value, c) => {
      if (value === null) {
        flush();
        return;
      }
      segment.push(`${xOf(c)},${yOf(value)}`);
    });
    flush();
    s.values.forEach((value, c) => {
      if (value === null) return;
      if (n > 24 && c !== n - 1) return;
      const dot = mk("circle", { cx: xOf(c), cy: yOf(value), r: 3, class: `cr-mark cr-fill-${sc(j)}`, "data-testid": "cr-chart-mark", "data-value": String(value), "data-category": model.categories[c] ?? "", "data-series": s.label }, svg);
      const title = mk("title", {}, dot);
      title.textContent = `${model.categories[c] ?? ""}${model.series.length > 1 ? `, ${s.label}` : ""}: ${exactValue(value, model.unit)}`;
    });
  });
}

function pie(svg: SVGElement, model: ChartModel, W: number): void {
  const cx = 150;
  const cy = H / 2;
  const radius = 100;
  const only = model.series[0];
  if (only === undefined) return;
  let angle = -Math.PI / 2;
  const slices = only.values.map((v, index) => ({ value: v ?? 0, index })).filter((s) => s.value > 0);
  for (const slice of slices) {
    const share = (model.shares[slice.index] ?? 0) / 10_000;
    const sweep = slice.value / model.total === 1 ? Math.PI * 2 - 0.0001 : (slice.value / model.total) * Math.PI * 2;
    const from = angle;
    const to = angle + sweep;
    angle = to;
    const large = sweep > Math.PI ? 1 : 0;
    const d =
      slices.length === 1
        ? `M ${cx} ${cy - radius} A ${radius} ${radius} 0 1 1 ${cx - 0.001} ${cy - radius} Z`
        : `M ${cx} ${cy} L ${cx + radius * Math.cos(from)} ${cy + radius * Math.sin(from)} A ${radius} ${radius} 0 ${large} 1 ${cx + radius * Math.cos(to)} ${cy + radius * Math.sin(to)} Z`;
    const path = mk("path", { d, class: `cr-mark cr-slice cr-fill-${sc(slice.index)}`, "data-testid": "cr-chart-mark", "data-value": String(slice.value), "data-category": model.categories[slice.index] ?? "", "data-share": String(model.shares[slice.index] ?? 0) }, svg);
    const title = mk("title", {}, path);
    title.textContent = `${model.categories[slice.index] ?? ""}: ${exactValue(slice.value, model.unit)}, ${(share * 100).toFixed(1)}%`;
  }
  // The key: each slice's name, its figure and its share, beside the pie.
  model.categories.forEach((category, index) => {
    const y = 36 + index * 24;
    mk("rect", { x: 300, y: y - 10, width: 12, height: 12, rx: 2, class: `cr-fill-${sc(index)}` }, svg);
    const name = text(svg, 320, y, fit(category, 42), "start", "cr-chart-label");
    if (category.length > 42) {
      const full = mk("title", {}, name);
      full.textContent = category;
    }
    text(svg, W - 12, y, `${exactValue(only.values[index] ?? 0, model.unit)}  ${((model.shares[index] ?? 0) / 100).toFixed(1)}%`, "end", "cr-chart-value");
  });
}

function drawSvg(model: ChartModel, W: number): SVGElement {
  const svg = mk("svg", {
    viewBox: `0 0 ${W} ${model.type === "number" ? 110 : H}`,
    role: "img",
    "aria-label": chartDescription(model),
    class: "cr-chart-svg",
    "data-testid": "cr-chart-svg",
    "data-kind": model.type,
  });
  if (model.type === "number") {
    text(svg, W / 2, 62, exactValue(model.total, model.unit), "middle", "cr-chart-big");
    text(svg, W / 2, 92, model.series[0]?.label ?? "", "middle", "cr-chart-label");
    return svg;
  }
  if (model.type === "pie") {
    pie(svg, model, W);
    return svg;
  }
  const plot = { x: PAD.left, y: PAD.top, w: W - PAD.left - PAD.right, h: H - PAD.top - PAD.bottom };
  const { min, max, ticks } = model.axis;
  const yOf = (v: number): number => plot.y + plot.h - ((v - min) / (max - min)) * plot.h;
  for (const tick of ticks) {
    const y = yOf(tick);
    mk("line", { x1: plot.x, x2: plot.x + plot.w, y1: y, y2: y, class: tick === 0 ? "cr-axis" : "cr-grid" }, svg);
    text(svg, plot.x - 8, y + 4, compactValue(tick, model.unit), "end", "cr-chart-tick");
  }
  const n = model.categories.length;
  const every = Math.max(1, Math.ceil(n / 12));
  model.categories.forEach((category, c) => {
    if (c % every !== 0) return;
    text(svg, plot.x + (plot.w / n) * c + plot.w / n / 2, H - 8, fit(category, Math.max(Math.floor((plot.w / n) * every / 5.5), 4)), "middle", "cr-chart-tick");
  });
  if (model.type === "line") lines(svg, model, plot, yOf);
  else bars(svg, model, plot, yOf);
  return svg;
}

/** The chart card: its title, the key, and the picture; or a short note when the model says there is nothing to draw. */
export function renderChartCard(result: ChartResult, width: number = CHART_WIDTH_BUILDER): HTMLElement {
  if (!result.ok) {
    return h("div", "cr-chart-card cr-chart-na", [h("span", "cr-chart-na-text", ["No data to chart."])], "cr-chart-na");
  }
  const model = result.model;
  const head = h("div", "cr-chart-head", [h("b", "cr-chart-title", [model.title], "cr-chart-title")]);
  const key = legend(model);
  if (key !== undefined) head.append(key);
  const card = h("div", "cr-chart-card", [head], "cr-chart");
  card.append(drawSvg(model, width) as unknown as Node);
  return card;
}
